import { useEffect, useMemo, useState } from 'react';
import {
  useStartKBConsolidation,
  useKBConsolidationRun,
  useApplyKBConsolidation,
  useRejectKBConsolidation,
} from '../../hooks/useKnowledgeBase';
import type {
  KBConsolidationRun,
  ArticleSnapshot,
} from '../../services/knowledgeBase.service';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2,
  Merge,
  Trash2,
  Pencil,
  FolderInput,
  FolderPlus,
  ArrowRight,
  AlertTriangle,
  RefreshCw,
  X,
} from 'lucide-react';
import type { ConsolidationExclude } from '../../services/knowledgeBase.service';
import { toastService } from '../../services/toast.service';

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="ml-auto shrink-0 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      title="Remove from proposal"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

function MergeCard({
  merge,
  articlesById,
  onRemove,
}: {
  merge: KBConsolidationRun['proposed_merges'][number];
  articlesById: Record<string, ArticleSnapshot>;
  onRemove: () => void;
}) {
  const survivor = articlesById[merge.survivor_article_id];
  const merged = merge.merged_article_ids
    .map((id) => articlesById[id])
    .filter(Boolean);

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Merge className="h-4 w-4 text-primary shrink-0" />
        <span className="font-medium text-sm truncate">
          {merge.new_name || survivor?.name || 'Merged article'}
        </span>
        <RemoveButton onClick={onRemove} />
      </div>
      <div className="text-xs text-muted-foreground">
        Merging {merged.length + 1} articles: {survivor?.name}
        {merged.map((m) => (
          <span key={m.article_id}> · <span className="line-through">{m.name}</span></span>
        ))}
      </div>
      {merge.rationale && (
        <p className="text-xs text-muted-foreground italic">{merge.rationale}</p>
      )}
    </div>
  );
}

function DeleteCard({
  del,
  articlesById,
  onRemove,
}: {
  del: KBConsolidationRun['proposed_deletes'][number];
  articlesById: Record<string, ArticleSnapshot>;
  onRemove: () => void;
}) {
  const article = articlesById[del.article_id];
  return (
    <div className="border border-dashed rounded-md p-3 space-y-1">
      <div className="flex items-center gap-2">
        <Trash2 className="h-4 w-4 text-destructive shrink-0" />
        <span className="text-sm line-through text-muted-foreground">
          {article?.name || del.article_id}
        </span>
        <RemoveButton onClick={onRemove} />
      </div>
      {del.rationale && (
        <p className="text-xs text-muted-foreground italic">{del.rationale}</p>
      )}
    </div>
  );
}

function EditCard({
  edit,
  articlesById,
  onRemove,
}: {
  edit: KBConsolidationRun['proposed_edits'][number];
  articlesById: Record<string, ArticleSnapshot>;
  onRemove: () => void;
}) {
  const article = articlesById[edit.article_id];
  const changes: string[] = [];
  if (edit.new_name) changes.push('title');
  if (edit.new_body) changes.push('content');
  if (edit.new_tags) changes.push('tags');

  return (
    <div className="border rounded-md p-3 space-y-1">
      <div className="flex items-center gap-2">
        <Pencil className="h-4 w-4 text-blue-600 shrink-0" />
        <span className="font-medium text-sm truncate">
          {article?.name || edit.article_id}
        </span>
        <span className="text-xs text-muted-foreground">
          ({changes.join(', ')})
        </span>
        <RemoveButton onClick={onRemove} />
      </div>
      {edit.new_name && (
        <div className="text-xs text-muted-foreground">
          Rename to: <span className="font-medium">{edit.new_name}</span>
        </div>
      )}
      {edit.rationale && (
        <p className="text-xs text-muted-foreground italic">{edit.rationale}</p>
      )}
    </div>
  );
}

function MoveCard({
  move,
  articlesById,
  run,
  onRemove,
}: {
  move: KBConsolidationRun['proposed_moves'][number];
  articlesById: Record<string, ArticleSnapshot>;
  run: KBConsolidationRun;
  onRemove: () => void;
}) {
  const article = articlesById[move.article_id];
  const newFolder = run.proposed_folder_creates.find(
    (f) => f.temp_id === move.new_parent_id
  );
  const destination = newFolder
    ? newFolder.name
    : move.new_parent_id
      ? `folder ${move.new_parent_id}`
      : 'Root';

  return (
    <div className="border rounded-md p-3 space-y-1">
      <div className="flex items-center gap-2">
        <FolderInput className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="text-sm truncate">
          {article?.name || move.article_id}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium truncate">{destination}</span>
        <RemoveButton onClick={onRemove} />
      </div>
      {move.rationale && (
        <p className="text-xs text-muted-foreground italic">{move.rationale}</p>
      )}
    </div>
  );
}

function FolderCreateCard({
  folder,
  onRemove,
}: {
  folder: KBConsolidationRun['proposed_folder_creates'][number];
  onRemove: () => void;
}) {
  return (
    <div className="border rounded-md p-3 space-y-1">
      <div className="flex items-center gap-2">
        <FolderPlus className="h-4 w-4 text-green-600 shrink-0" />
        <span className="font-medium text-sm">{folder.name}</span>
        <RemoveButton onClick={onRemove} />
      </div>
      {folder.rationale && (
        <p className="text-xs text-muted-foreground italic">{folder.rationale}</p>
      )}
    </div>
  );
}

export function ConsolidateKBDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [runId, setRunId] = useState<string | null>(null);
  const [removedMerges, setRemovedMerges] = useState<Set<number>>(new Set());
  const [removedDeletes, setRemovedDeletes] = useState<Set<number>>(new Set());
  const [removedEdits, setRemovedEdits] = useState<Set<number>>(new Set());
  const [removedMoves, setRemovedMoves] = useState<Set<number>>(new Set());
  const [removedFolderCreates, setRemovedFolderCreates] = useState<Set<number>>(new Set());

  const startMutation = useStartKBConsolidation();
  const { data: run } = useKBConsolidationRun(runId);
  const applyMutation = useApplyKBConsolidation();
  const rejectMutation = useRejectKBConsolidation();

  const clearRemovals = () => {
    setRemovedMerges(new Set());
    setRemovedDeletes(new Set());
    setRemovedEdits(new Set());
    setRemovedMoves(new Set());
    setRemovedFolderCreates(new Set());
  };

  const toggleRemoved = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, idx: number) => {
    setter((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };

  useEffect(() => {
    if (open && !runId && !startMutation.isPending) {
      startMutation.mutate(false, {
        onSuccess: (created) => setRunId(created.id),
      });
    }
    if (!open) {
      setRunId(null);
      clearRemovals();
      startMutation.reset();
      applyMutation.reset();
      rejectMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const articlesById = useMemo(() => {
    const map: Record<string, ArticleSnapshot> = {};
    (run?.original_articles || []).forEach((a) => {
      map[a.article_id] = a;
    });
    return map;
  }, [run?.original_articles]);

  const status = run?.status;
  const isWorking = startMutation.isPending || !run || status === 'pending';
  const isReady = status === 'ready';
  const isApplied = status === 'applied';

  const totalChanges = useMemo(() => {
    if (!run) return 0;
    return (
      (run.proposed_merges.length - removedMerges.size) +
      (run.proposed_deletes.length - removedDeletes.size) +
      (run.proposed_edits.length - removedEdits.size) +
      (run.proposed_moves.length - removedMoves.size) +
      (run.proposed_folder_creates.length - removedFolderCreates.size)
    );
  }, [run, removedMerges, removedDeletes, removedEdits, removedMoves, removedFolderCreates]);

  const handleApply = () => {
    if (!runId) return;
    const exclude: ConsolidationExclude = {};
    if (removedMerges.size) exclude.merges = [...removedMerges];
    if (removedDeletes.size) exclude.deletes = [...removedDeletes];
    if (removedEdits.size) exclude.edits = [...removedEdits];
    if (removedMoves.size) exclude.moves = [...removedMoves];
    if (removedFolderCreates.size) exclude.folder_creates = [...removedFolderCreates];
    const hasExclusions = Object.keys(exclude).length > 0;
    applyMutation.mutate(
      { runId, exclude: hasExclusions ? exclude : undefined },
      { onSuccess: () => {
        toastService.success('Knowledge base updated successfully');
        onOpenChange(false);
      }}
    );
  };

  const handleRecycle = () => {
    setRunId(null);
    clearRemovals();
    startMutation.mutate(true, {
      onSuccess: (created) => setRunId(created.id),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Consolidate Knowledge Base</DialogTitle>
          <DialogDescription>
            Ellie reviews the knowledge base to merge redundant articles, tighten
            verbose content, improve tags, and reorganize folders. Edits and
            merges are proposed as pending updates for your review. Nothing
            changes until you apply.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isWorking && (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Analyzing knowledge base articles…
              </p>
            </div>
          )}

          {status === 'failed' && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
              <div>
                <p className="text-destructive text-sm font-medium">
                  Consolidation failed
                </p>
                <p className="text-destructive text-xs mt-1">{run?.error}</p>
              </div>
            </div>
          )}

          {isReady && totalChanges === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No consolidation changes needed — the knowledge base looks good.
            </p>
          )}

          {isReady && totalChanges > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="secondary">
                  {run!.original_articles.length} articles
                </Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="default">{totalChanges} proposed changes</Badge>
              </div>

              <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
                {run!.proposed_merges.length > 0 && run!.proposed_merges.some((_, i) => !removedMerges.has(i)) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Merge className="h-4 w-4" />
                      Merges ({run!.proposed_merges.length - removedMerges.size})
                    </div>
                    {run!.proposed_merges.map((m, i) =>
                      removedMerges.has(i) ? null : (
                        <MergeCard key={i} merge={m} articlesById={articlesById} onRemove={() => toggleRemoved(setRemovedMerges, i)} />
                      )
                    )}
                  </div>
                )}

                {run!.proposed_edits.length > 0 && run!.proposed_edits.some((_, i) => !removedEdits.has(i)) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Pencil className="h-4 w-4" />
                      Edits ({run!.proposed_edits.length - removedEdits.size})
                    </div>
                    {run!.proposed_edits.map((e, i) =>
                      removedEdits.has(i) ? null : (
                        <EditCard key={i} edit={e} articlesById={articlesById} onRemove={() => toggleRemoved(setRemovedEdits, i)} />
                      )
                    )}
                  </div>
                )}

                {run!.proposed_deletes.length > 0 && run!.proposed_deletes.some((_, i) => !removedDeletes.has(i)) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Trash2 className="h-4 w-4" />
                      Deletes ({run!.proposed_deletes.length - removedDeletes.size})
                    </div>
                    {run!.proposed_deletes.map((d, i) =>
                      removedDeletes.has(i) ? null : (
                        <DeleteCard key={i} del={d} articlesById={articlesById} onRemove={() => toggleRemoved(setRemovedDeletes, i)} />
                      )
                    )}
                  </div>
                )}

                {run!.proposed_folder_creates.length > 0 && run!.proposed_folder_creates.some((_, i) => !removedFolderCreates.has(i)) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <FolderPlus className="h-4 w-4" />
                      New Folders ({run!.proposed_folder_creates.length - removedFolderCreates.size})
                    </div>
                    {run!.proposed_folder_creates.map((f, i) =>
                      removedFolderCreates.has(i) ? null : (
                        <FolderCreateCard key={i} folder={f} onRemove={() => toggleRemoved(setRemovedFolderCreates, i)} />
                      )
                    )}
                  </div>
                )}

                {run!.proposed_moves.length > 0 && run!.proposed_moves.some((_, i) => !removedMoves.has(i)) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <FolderInput className="h-4 w-4" />
                      Moves ({run!.proposed_moves.length - removedMoves.size})
                    </div>
                    {run!.proposed_moves.map((m, i) =>
                      removedMoves.has(i) ? null : (
                        <MoveCard key={i} move={m} articlesById={articlesById} run={run!} onRemove={() => toggleRemoved(setRemovedMoves, i)} />
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {(isReady || status === 'failed') && (
            <Button
              variant="ghost"
              className="sm:mr-auto"
              onClick={handleRecycle}
              disabled={startMutation.isPending || applyMutation.isPending}
              title="Discard this proposal and run a fresh consolidation"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${
                  startMutation.isPending ? 'animate-spin' : ''
                }`}
              />
              Recycle
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applyMutation.isPending}
          >
            Cancel
          </Button>
          {isReady && totalChanges > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  runId &&
                  rejectMutation.mutate(runId, {
                    onSuccess: () => onOpenChange(false),
                  })
                }
                disabled={applyMutation.isPending || rejectMutation.isPending}
              >
                Reject
              </Button>
              <Button onClick={handleApply} disabled={applyMutation.isPending}>
                {applyMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Apply Consolidation
              </Button>
            </>
          )}
          {isApplied && (
            <Badge variant="success" className="self-center">
              Applied
            </Badge>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
